import Button from 'devextreme-react/button';
import CheckBox from 'devextreme-react/check-box';
import DataGrid, { Column, Scrolling, SearchPanel } from 'devextreme-react/data-grid';
import LoadIndicator from 'devextreme-react/load-indicator';
import Popup from 'devextreme-react/popup';
import TextArea from 'devextreme-react/text-area';
import TextBox from 'devextreme-react/text-box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createAsciiArtItem,
  deactivateAsciiArtItem,
  listAsciiArtAdmin,
  listAsciiArtCategories,
  updateAsciiArtItem,
  type AsciiArtItem,
  type AsciiArtUpsertRequest
} from '../../../api/asciiArt';
import { showErrorToast, showSuccessToast } from '@jarvis/platform-ui';
import '../../../apps/utilityApps.css';
import './asciiArtAdmin.css';

const PAGE_SIZE = 50;

type EditorState = {
  mode: 'create' | 'edit';
  id?: string;
  name: string;
  art: string;
  category: string;
  subcategory: string;
  tags: string;
  isActive: boolean;
};

const emptyEditor = (): EditorState => ({
  mode: 'create',
  name: '',
  art: '',
  category: 'Reactions',
  subcategory: '',
  tags: '',
  isActive: true
});

function parseTags(raw: string): string[] {
  return raw
    .split(/[,;\n]/)
    .map((t) => t.trim())
    .filter(Boolean);
}

export function AsciiArtAdminPage() {
  const [items, setItems] = useState<AsciiArtItem[]>([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [category, setCategory] = useState('all');
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editor, setEditor] = useState<EditorState>(emptyEditor);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [q]);

  const reloadCategories = useCallback(async () => {
    try {
      const cats = await listAsciiArtCategories();
      setCategories(cats.map((c) => c.name));
    } catch {
      /* grid reload surfaces errors */
    }
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const page = await listAsciiArtAdmin({
        q: debouncedQ || undefined,
        category: category === 'all' ? undefined : category,
        skip,
        take: PAGE_SIZE
      });
      setItems(page.items);
      setTotal(page.total);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not load ASCII art library.');
    } finally {
      setLoading(false);
    }
  }, [category, debouncedQ, skip]);

  useEffect(() => {
    void reloadCategories();
  }, [reloadCategories]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const categoryChips = useMemo(() => ['all', ...categories], [categories]);

  function openCreate() {
    setEditor(emptyEditor());
    setEditorOpen(true);
  }

  function openEdit(row: AsciiArtItem) {
    setEditor({
      mode: 'edit',
      id: row.id,
      name: row.name,
      art: row.art,
      category: row.category,
      subcategory: row.subcategory ?? '',
      tags: (row.tags ?? []).join(', '),
      isActive: row.isActive
    });
    setEditorOpen(true);
  }

  async function saveEditor() {
    const body: AsciiArtUpsertRequest = {
      name: editor.name.trim(),
      art: editor.art.replace(/\r\n/g, '\n').trimEnd(),
      category: editor.category.trim(),
      subcategory: editor.subcategory.trim() || null,
      tags: parseTags(editor.tags),
      isActive: editor.isActive
    };
    if (!body.name || !body.art || !body.category) {
      showErrorToast('Name, art, and category are required.');
      return;
    }

    setBusy(true);
    try {
      if (editor.mode === 'create') {
        await createAsciiArtItem(body);
        showSuccessToast('ASCII item created.');
      } else if (editor.id) {
        await updateAsciiArtItem(editor.id, body);
        showSuccessToast('ASCII item updated.');
      }
      setEditorOpen(false);
      await reload();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not save ASCII item.');
    } finally {
      setBusy(false);
    }
  }

  async function onDeactivate(row: AsciiArtItem) {
    setBusy(true);
    try {
      await deactivateAsciiArtItem(row.id);
      showSuccessToast('ASCII item deactivated.');
      await reload();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not deactivate item.');
    } finally {
      setBusy(false);
    }
  }

  const pageIndex = Math.floor(skip / PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="ops-fill-page ascii-art-admin">
      <div className="ops-fill-page__header">
        <div>
          <h1 className="utility-app__title" style={{ margin: 0 }}>
            ASCII Art Library
          </h1>
          <p className="utility-app__muted" style={{ margin: '0.25rem 0 0' }}>
            Seeded catalog is insert-only. Create or edit items here without reseeding.
          </p>
        </div>
        <div className="utility-app__toolbar-group">
          {loading || busy ? <LoadIndicator visible height={18} width={18} /> : null}
          <Button text="Add item" type="default" icon="plus" onClick={openCreate} />
          <Button text="Refresh" icon="refresh" stylingMode="outlined" onClick={() => void reload()} />
        </div>
      </div>

      <div className="ops-fill-page__panel ascii-art-admin__panel">
        <div className="ascii-art-admin__filters">
          <div className="emoji-kb__cats" role="tablist" aria-label="Filter by category">
            {categoryChips.map((name) => (
              <Button
                key={name}
                text={name === 'all' ? 'All' : name}
                stylingMode={category === name ? 'contained' : 'outlined'}
                type={category === name ? 'default' : 'normal'}
                onClick={() => {
                  setSkip(0);
                  setCategory(name);
                }}
              />
            ))}
          </div>
          <TextBox
            value={q}
            width="100%"
            mode="search"
            placeholder="Search name, art, tags…"
            showClearButton
            onValueChanged={(e) => {
              setSkip(0);
              setQ(String(e.value ?? ''));
            }}
          />
          <div className="ascii-art-admin__pager">
            <span className="utility-app__muted">
              Showing {items.length ? skip + 1 : 0}–{skip + items.length} of {total}
            </span>
            <div className="ascii-art-admin__pager-actions">
              <Button
                text="Previous"
                stylingMode="outlined"
                disabled={pageIndex <= 0 || loading}
                onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              />
              <span className="utility-app__muted">
                Page {pageIndex + 1} / {pageCount}
              </span>
              <Button
                text="Next"
                stylingMode="outlined"
                disabled={pageIndex + 1 >= pageCount || loading}
                onClick={() => setSkip(skip + PAGE_SIZE)}
              />
            </div>
          </div>
        </div>

        <div className="ops-fill-page__grid-host">
          <DataGrid
            dataSource={items}
            keyExpr="id"
            showBorders
            rowAlternationEnabled
            hoverStateEnabled
            height="100%"
            columnAutoWidth
            wordWrapEnabled
            paging={{ enabled: false }}
            onRowDblClick={(e) => {
              if (e.data) openEdit(e.data as AsciiArtItem);
            }}
          >
            <Scrolling mode="standard" showScrollbar="always" useNative />
            <SearchPanel visible={false} />
            <Column dataField="name" caption="Name" width={180} />
            <Column dataField="art" caption="Art" minWidth={220} />
            <Column dataField="category" caption="Category" width={140} />
            <Column dataField="subcategory" caption="Subcategory" width={140} />
            <Column dataField="isActive" caption="Active" width={80} dataType="boolean" />
            <Column dataField="origin" caption="Origin" width={90} />
            <Column
              caption="Actions"
              width={180}
              allowSorting={false}
              cellRender={(cell) => {
                const row = cell.data as AsciiArtItem;
                return (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Button text="Edit" stylingMode="text" onClick={() => openEdit(row)} />
                    {row.isActive ? (
                      <Button
                        text="Deactivate"
                        stylingMode="text"
                        type="danger"
                        onClick={() => void onDeactivate(row)}
                      />
                    ) : null}
                  </div>
                );
              }}
            />
          </DataGrid>
        </div>
      </div>

      <Popup
        visible={editorOpen}
        onHiding={() => setEditorOpen(false)}
        dragEnabled
        showCloseButton
        title={editor.mode === 'create' ? 'Add ASCII art' : 'Edit ASCII art'}
        width={560}
        height="auto"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '0.25rem 0' }}>
          <TextBox
            value={editor.name}
            label="Name"
            stylingMode="outlined"
            onValueChanged={(e) => setEditor((prev) => ({ ...prev, name: String(e.value ?? '') }))}
          />
          <TextArea
            value={editor.art}
            label="Art (max 3 lines)"
            stylingMode="outlined"
            height={110}
            onValueChanged={(e) => setEditor((prev) => ({ ...prev, art: String(e.value ?? '') }))}
          />
          <TextBox
            value={editor.category}
            label="Category"
            stylingMode="outlined"
            onValueChanged={(e) =>
              setEditor((prev) => ({ ...prev, category: String(e.value ?? '') }))
            }
          />
          <TextBox
            value={editor.subcategory}
            label="Subcategory"
            stylingMode="outlined"
            onValueChanged={(e) =>
              setEditor((prev) => ({ ...prev, subcategory: String(e.value ?? '') }))
            }
          />
          <TextBox
            value={editor.tags}
            label="Tags (comma-separated)"
            stylingMode="outlined"
            onValueChanged={(e) => setEditor((prev) => ({ ...prev, tags: String(e.value ?? '') }))}
          />
          <CheckBox
            value={editor.isActive}
            text="Active"
            onValueChanged={(e) =>
              setEditor((prev) => ({ ...prev, isActive: Boolean(e.value) }))
            }
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button text="Cancel" stylingMode="outlined" onClick={() => setEditorOpen(false)} />
            <Button text="Save" type="default" disabled={busy} onClick={() => void saveEditor()} />
          </div>
        </div>
      </Popup>
    </div>
  );
}
